import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Phone, Wrench, Zap, Thermometer } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { CallNowButton, EstimateButton } from "@/components/CTAButtons";
import { CTABand } from "@/components/Layout";
import { TrustBar, ProjectGallery } from "@/components/Sections";
import LeadForm from "@/components/LeadForm";
import { getService, business } from "@/data/site";
import { track } from "@/lib/analytics";

const icons = { Wrench, Zap, Thermometer };

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getService(slug);

  useSeo({
    title: service?.seoTitle,
    description: service?.seoDesc,
    jsonLd: service && {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: `${service.name} services`,
      areaServed: "New York, NY",
      provider: { "@type": "LocalBusiness", name: business.name, telephone: business.phoneDisplay },
    },
  });

  if (!service) return <Navigate to="/services" replace />;
  const Icon = icons[service.icon];

  return (
    <>
      {/* HERO */}
      <section className="relative bg-[#0F2E4C] text-white overflow-hidden" data-testid="service-hero">
        <img src={service.image} alt={`${service.name} services in New York`} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2E4C] via-[#0F2E4C]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#059669] px-4 py-2 mb-6">
              <Icon className="w-5 h-5" /> <span className="text-sm font-bold uppercase tracking-wide">{service.tagline}</span>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
              {service.name} Services in New York
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 mb-8">{service.short}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <CallNowButton size="lg" label={`Call ${business.phoneDisplay}`} testid="service-call-btn" />
              <EstimateButton size="lg" testid="service-estimate-btn" />
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* OVERVIEW + PROBLEMS */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid lg:grid-cols-2 gap-12" data-testid="service-overview">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">Overview</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-5">Expert {service.name} You Can Rely On</h2>
          <p className="text-[#475569] text-lg leading-relaxed mb-8">{service.overview}</p>
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-4">Common Problems We Solve</p>
          <ul className="grid sm:grid-cols-2 gap-3">
            {service.problems.map((p) => (
              <li key={p} className="flex items-start gap-2 text-[#0F2E4C]"><CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0 mt-0.5" /> {p}</li>
            ))}
          </ul>
        </div>
        <div className="bg-[#0F2E4C] text-white p-8 h-fit">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#34D399] mb-4">Why Choose Empire</p>
          <ul className="space-y-4 mb-8">
            {service.reasons.map((r) => (
              <li key={r} className="flex items-start gap-3"><div className="w-6 h-6 bg-[#059669] flex items-center justify-center shrink-0 mt-0.5"><CheckCircle2 className="w-4 h-4" /></div> <span className="text-slate-200">{r}</span></li>
            ))}
          </ul>
          <div className="border-t border-slate-700 pt-6">
            <p className="font-bold mb-2">Need help now?</p>
            <a href={business.phoneHref} onClick={() => track("phone_tap", { location: "service" })} className="flex items-center gap-2 text-2xl font-display font-black text-[#34D399]" data-testid="service-phone-link"><Phone className="w-6 h-6" /> {business.phoneDisplay}</a>
          </div>
        </div>
      </section>

      {/* PROJECTS for this service */}
      <section className="bg-white border-y border-[#E2E8F0]" data-testid="service-projects">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">Our Work</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">{service.name} Transformations</h2>
            <p className="text-[#475569] text-lg">Drag the slider to see the results.</p>
          </div>
          <ProjectGallery showFilter={false} filterCategory={service.slug} />
        </div>
      </section>

      {/* RELATED + FORM */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid lg:grid-cols-2 gap-12" data-testid="service-related">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">Related Services</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">One Call Handles It All</h2>
          <p className="text-[#475569] text-lg mb-6">Homes rarely have just one issue. Explore our other services — and remember, one trusted team can handle them all.</p>
          <div className="space-y-4">
            {service.related.map((rslug) => {
              const rs = getService(rslug);
              const RIcon = icons[rs.icon];
              return (
                <Link key={rslug} to={`/services/${rslug}`} className="group flex items-center gap-4 bg-white border border-[#E2E8F0] p-5 hover:border-[#0F2E4C] hover:-translate-y-1 hover:shadow-[4px_4px_0px_#0F2E4C] transition-all" data-testid={`related-${rslug}`}>
                  <div className="w-12 h-12 bg-[#059669] flex items-center justify-center shrink-0"><RIcon className="w-6 h-6 text-white" /></div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-xl">{rs.name}</h3>
                    <p className="text-sm text-[#475569]">{rs.tagline}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#0F2E4C] group-hover:translate-x-1 transition-transform" />
                </Link>
              );
            })}
          </div>
        </div>
        <div className="bg-[#FAFAFA] border-2 border-[#0F2E4C] p-6 sm:p-8">
          <h3 className="font-display font-bold text-2xl mb-1">Request a Free {service.name} Estimate</h3>
          <p className="text-[#475569] mb-6">Fast response. No obligation.</p>
          <LeadForm compact defaultService={service.name} />
        </div>
      </section>

      <CTABand heading={`Need ${service.name} Help in New York?`} sub="Call now or request your free estimate. We respond fast — including nights, weekends, and holidays." />
    </>
  );
};

export default ServiceDetail;
